import { createApi } from '@reduxjs/toolkit/query/react';
import { normalizePublicArtifactCollectionResponse } from 'api/ArtifactsApi';
import { baseQueryWithReAuth } from 'api/Api';
import { DesignProps } from 'api/DesignApi';
import { normalizePublicLifeStory, PublicLifeStory } from 'api/LifeReviewApi';
import { FileMessage, Message, normalizeMessage } from 'api/MessagesApi';
import { Artifact, PublicArtifactCollection } from 'components/Artifacts/types';

export interface Inbox {
  id: string;
  message: Message;
  messageFile?: FileMessage;
  designProps?: DesignProps;
  createdAt: string;
  updatedAt: string;
}

export type SharedArtifactSummary = {
  id: string;
  shareId: string;
  title: string;
  description?: string | null;
  itemCount: number;
  senderName?: string;
  sharedAt?: string | null;
  designProps?: DesignProps;
};

export type SharedLifeStorySummary = {
  id: string;
  inviteId: string;
  lifeStory: PublicLifeStory;
  senderName?: string;
  sharedAt?: string | null;
  designProps?: DesignProps;
};

export type SharedWithMeSection<T> = {
  totalCount: number;
  items: T[];
};

export type SharedWithMeOverview = {
  messages: SharedWithMeSection<Inbox>;
  artifacts: SharedWithMeSection<SharedArtifactSummary>;
  lifeStories: SharedWithMeSection<SharedLifeStorySummary>;
};

type Envelope = {
  data?: unknown;
};

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const joinNonEmpty = (parts: string[]) => parts.filter(Boolean).join(' ').trim();

const asNullableString = (value: unknown): string | null => {
  const normalized = asString(value).trim();
  return normalized || null;
};

const getEnvelopeData = (response: unknown) => asRecord(asRecord(response).data);

const asNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : Number(asString(value)) || 0);

const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

const pickFirstStringValue = (...values: Array<string | null | undefined>) => values.find((value) => Boolean(value?.trim()))?.trim() || '';

const getDisplayName = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  const source = asRecord(value);

  return pickFirstStringValue(
    asString(source.displayName),
    asString(source.fullName),
    asString(source.patientDisplayName),
    asString(source.beneficiaryDisplayName),
    asString(source.name),
    joinNonEmpty([asString(source.firstName), asString(source.lastName)])
  );
};

const normalizeDesignProps = (...values: unknown[]): DesignProps | undefined => {
  for (const value of values) {
    const source = asRecord(value);
    const colorPrimary = asString(source.colorPrimary);
    const colorPrimaryLighter = asString(source.colorPrimaryLighter);
    const companyName = pickFirstStringValue(asString(source.companyName), asString(source.tenantOrganizationName));
    const description = asString(source.description);
    const email = asString(source.email);
    const logoUrl = pickFirstStringValue(asString(source.logoUrl), asString(source.tenantOrganizationLogoUrl));
    const footerText = asString(source.footerText);
    const domainUrl = asString(source.domainUrl);
    const privacyUrl = asString(source.privacyUrl);
    const termsUrl = asString(source.termsUrl);
    const donationUrl = asString(source.donationUrl);
    const volunteerUrl = asString(source.volunteerUrl);

    if (
      companyName ||
      colorPrimary ||
      colorPrimaryLighter ||
      description ||
      email ||
      logoUrl ||
      footerText ||
      domainUrl ||
      privacyUrl ||
      termsUrl ||
      donationUrl ||
      volunteerUrl
    ) {
      return {
        ...(companyName ? { companyName } : {}),
        ...(description ? { description } : {}),
        ...(colorPrimary ? { colorPrimary } : {}),
        ...(colorPrimaryLighter ? { colorPrimaryLighter } : {}),
        ...(email ? { email } : {}),
        ...(logoUrl ? { logoUrl } : {}),
        ...(footerText ? { footerText } : {}),
        ...(domainUrl ? { domainUrl } : {}),
        ...(privacyUrl ? { privacyUrl } : {}),
        ...(termsUrl ? { termsUrl } : {}),
        ...(donationUrl ? { donationUrl } : {}),
        ...(volunteerUrl ? { volunteerUrl } : {})
      };
    }
  }

  return undefined;
};

const pickFirstArray = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (Array.isArray(source[key])) {
      return source[key] as unknown[];
    }
  }

  return [];
};

const normalizeSharedSection = <T>(
  data: Record<string, unknown>,
  sectionKeys: string[],
  mapItem: (item: unknown, index: number) => T | null,
  nestedKeys: string[] = ['items']
): SharedWithMeSection<T> => {
  for (const key of sectionKeys) {
    const rawSection = data[key];
    const section = asRecord(rawSection);
    const itemsSource = Array.isArray(rawSection) ? (rawSection as unknown[]) : pickFirstArray(section, nestedKeys);
    const items = itemsSource.map(mapItem).filter(Boolean) as T[];

    if (Array.isArray(rawSection) || Object.keys(section).length > 0) {
      return {
        totalCount: asNumber(section.totalCount) || items.length,
        items
      };
    }
  }

  return {
    totalCount: 0,
    items: []
  };
};

const normalizeInboxItem = (item: unknown): Inbox => {
  const source = asRecord(item);
  const messageSource = asRecord(source.message);
  const messageFileSource = asRecord(source.messageFile);
  const senderSource = asRecord(source.sender);
  const senderDisplayName = asString(senderSource.displayName).trim();
  const [senderFirstName = '', ...senderRest] = senderDisplayName ? senderDisplayName.split(/\s+/) : [];
  const senderLastName = senderRest.join(' ');
  const rawMessageUser = asRecord(messageSource.user);
  const createdAt = asString(source.createdAt) || asString(messageSource.createdAt);
  const updatedAt = asString(source.updatedAt) || asString(messageSource.updatedAt) || createdAt;
  const id =
    asString(source.id) ||
    asString(source.inboxId) ||
    asString(source.messageContactId) ||
    asString(source.messageRecipientId) ||
    asString(messageSource.id);
  const senderUser = Object.keys(senderSource).length
    ? ({
        ...(senderSource as Partial<Message['user']>),
        firstName: asString(senderSource.firstName) || senderFirstName,
        lastName: asString(senderSource.lastName) || senderLastName,
        email: asString(senderSource.email)
      } as Message['user'])
    : undefined;
  const messageUser = Object.keys(rawMessageUser).length
    ? ({
        ...senderUser,
        ...(rawMessageUser as Partial<Message['user']>),
        firstName: asString(rawMessageUser.firstName) || senderUser?.firstName || '',
        lastName: asString(rawMessageUser.lastName) || senderUser?.lastName || '',
        email: asString(rawMessageUser.email) || senderUser?.email || ''
      } as Message['user'])
    : senderUser;
  const normalizedMessage = normalizeMessage({
    id: asString(messageSource.id) || id,
    userId: asNullableString(messageSource.userId),
    patientProfileId: asNullableString(messageSource.patientProfileId),
    patientProfile: (messageSource.patientProfile as Message['patientProfile']) || null,
    type: (asString(messageSource.type) || 'text') as Message['type'],
    status: (asString(messageSource.status) || 'published') as Message['status'],
    name: asString(messageSource.name) || asString(source.name),
    content: asString(messageSource.content) || asString(source.content),
    contacts: (Array.isArray(messageSource.contacts) ? messageSource.contacts : []) as Message['contacts'],
    eventType: asString(messageSource.eventType),
    ...(asString(messageSource.eventHint) ? { eventHint: asString(messageSource.eventHint) } : {}),
    deliveryYear: typeof messageSource.deliveryYear === 'number' ? messageSource.deliveryYear : null,
    deliveryMonth: typeof messageSource.deliveryMonth === 'number' ? messageSource.deliveryMonth : null,
    deliveryDay: typeof messageSource.deliveryDay === 'number' ? messageSource.deliveryDay : null,
    fileName: asString(messageSource.fileName) || asString(messageFileSource.fileName),
    ...(messageUser ? { user: messageUser } : {}),
    ...(asStringArray(messageSource.aiTones).length ? { aiTones: asStringArray(messageSource.aiTones) } : {}),
    aiFocus: asString(messageSource.aiFocus),
    aiMemory: asString(messageSource.aiMemory),
    aiClosing: asString(messageSource.aiClosing),
    aiOtherInfo: asString(messageSource.aiOtherInfo),
    createdAt,
    updatedAt
  } as Message);
  const normalizedMessageFile = Object.keys(messageFileSource).length
    ? ({
        fileName: asString(messageFileSource.fileName) || normalizedMessage.fileName || undefined,
        signedUrl: asString(messageFileSource.signedUrl) || undefined
      } as FileMessage)
    : undefined;

  return {
    id,
    message: normalizedMessage,
    ...(normalizedMessageFile ? { messageFile: normalizedMessageFile } : {}),
    ...(source.designProps ? { designProps: source.designProps as DesignProps } : {}),
    createdAt,
    updatedAt
  };
};

const toLifeStory = (value: unknown): PublicLifeStory => normalizePublicLifeStory(value);

const getFirstArtifact = (source: Record<string, unknown>): Artifact | null => {
  const artifact = asRecord(source.artifact);
  if (artifact.id) {
    return artifact as Artifact;
  }

  const collection = asRecord(source.collection);
  const items = pickFirstArray(collection, ['items', 'artifacts']);
  return items.length ? (asRecord(items[0]) as Artifact) : null;
};

const getArtifactShareId = (source: Record<string, unknown>) => {
  const share = asRecord(source.share);
  return asString(source.shareId) || asString(source.artifactShareId) || asString(source.id) || asString(share.id);
};

const normalizeSharedArtifactSummary = (item: unknown, index: number): SharedArtifactSummary | null => {
  const source = asRecord(item);
  const artifact = getFirstArtifact(source);
  const artifactSource = asRecord(artifact as unknown);
  const collection = asRecord(source.collection);
  const items = pickFirstArray(collection, ['items', 'artifacts']);
  const shareId = getArtifactShareId(source);
  const share = asRecord(source.share);
  const branding = asRecord(source.branding);
  const title =
    asString(source.title) ||
    asString(source.name) ||
    artifact?.title ||
    asString(branding.heroTitle) ||
    'Shared artifacts';

  if (!shareId) {
    return null;
  }

  return {
    id: shareId || artifact?.id || `shared-artifact-${index}`,
    shareId,
    title,
    description:
      asNullableString(source.description) ||
      artifact?.description ||
      asNullableString(branding.heroSubtitle) ||
      null,
    itemCount: items.length || (artifact ? 1 : 0),
    senderName: pickFirstStringValue(
      asString(source.patientDisplayName),
      getDisplayName(source.sender),
      getDisplayName(source.sharedBy),
      getDisplayName(source.owner),
      getDisplayName(source.createdBy),
      getDisplayName(source.patient),
      asString(share.patientDisplayName),
      getDisplayName(share.sender),
      getDisplayName(share.sharedBy),
      getDisplayName(share.owner),
      getDisplayName(share.patient),
      asString(collection.patientDisplayName),
      getDisplayName(collection.patient),
      asString(artifactSource.patientDisplayName),
      getDisplayName(artifactSource.patient),
      getDisplayName(share),
      getDisplayName(branding)
    ) || undefined,
    sharedAt: asNullableString(source.sharedAt) || asNullableString(share.createdAt) || asNullableString(source.createdAt),
    designProps: normalizeDesignProps(source.designProps, share.designProps, branding)
  };
};

const getLifeStoryInviteId = (source: Record<string, unknown>) => {
  const invite = asRecord(source.invite);
  return asString(source.inviteId) || asString(source.lifeStoryInviteId) || asString(source.id) || asString(invite.id);
};

const getLifeStorySource = (source: Record<string, unknown>) => {
  const nestedLifeStory = asRecord(source.lifeStory);
  if (nestedLifeStory.id || nestedLifeStory.fullName || nestedLifeStory.heroTitle || nestedLifeStory.firstName || nestedLifeStory.lastName) {
    return nestedLifeStory;
  }

  return source;
};

const normalizeSharedLifeStorySummary = (item: unknown, index: number): SharedLifeStorySummary | null => {
  const source = asRecord(item);
  const inviteId = getLifeStoryInviteId(source);
  if (!inviteId) {
    return null;
  }

  const lifeStorySource = getLifeStorySource(source);
  const invite = asRecord(source.invite);
  const branding = asRecord(source.branding);

  return {
    id: asString(lifeStorySource.id) || inviteId || `shared-life-story-${index}`,
    inviteId,
    lifeStory: toLifeStory({ ...source, lifeStory: lifeStorySource }),
    senderName: pickFirstStringValue(
      getDisplayName(source.sender),
      getDisplayName(source.sharedBy),
      getDisplayName(invite),
      getDisplayName(lifeStorySource)
    ) || undefined,
    sharedAt: asNullableString(source.sharedAt) || asNullableString(invite.createdAt) || asNullableString(source.createdAt),
    designProps: normalizeDesignProps(source.designProps, invite.designProps, lifeStorySource.designProps, branding)
  };
};

export const normalizeSharedWithMeOverview = (response: Envelope): SharedWithMeOverview => {
  const data = getEnvelopeData(response);

  return {
    messages: normalizeSharedSection(data, ['messages', 'inbox', 'messageShares'], (item) => {
      const normalized = normalizeInboxItem(item);
      return normalized.id ? normalized : null;
    }),
    artifacts: normalizeSharedSection(data, ['artifacts', 'artifactShares', 'sharedArtifacts'], normalizeSharedArtifactSummary),
    lifeStories: normalizeSharedSection(data, ['lifeStories', 'lifeStoryInvites', 'sharedLifeStories'], normalizeSharedLifeStorySummary)
  };
};

const normalizeSharedLifeStoryResponse = (response: Envelope): PublicLifeStory => {
  const data = getEnvelopeData(response);
  return toLifeStory(data);
};

export const inboxApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'inboxApi',
  tagTypes: ['Inbox'],
  endpoints: (build) => ({
    getAllInbox: build.query<{ list: Inbox[]; total: number }, { limit?: number; offset?: number }>({
      query: (params) => ({ url: '/inbox', params }),
      transformResponse: (response: { data: [Inbox[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'Inbox' as const,
                id
              })),
              { type: 'Inbox', id: 'LIST' }
            ]
          : [{ type: 'Inbox', id: 'LIST' }]
    }),
    getOneInbox: build.query<Inbox, Pick<Inbox, 'id'>>({
      query: ({ id }) => ({ url: `/inbox/${id}` }),
      transformResponse: (response: Envelope) => normalizeInboxItem(getEnvelopeData(response)),
      providesTags: (result, error, { id }) => [{ type: 'Inbox', id }]
    }),
    getSharedWithMeOverview: build.query<SharedWithMeOverview, void>({
      query: () => ({ url: '/inbox/shared-with-me' }),
      transformResponse: normalizeSharedWithMeOverview
    }),
    getSharedArtifactCollection: build.query<PublicArtifactCollection, { shareId: string }>({
      query: ({ shareId }) => ({ url: `/inbox/shared-with-me/artifacts/${encodeURIComponent(shareId)}` }),
      transformResponse: (response: Parameters<typeof normalizePublicArtifactCollectionResponse>[0]) =>
        normalizePublicArtifactCollectionResponse(response)
    }),
    getSharedLifeStory: build.query<PublicLifeStory, { inviteId: string }>({
      query: ({ inviteId }) => ({ url: `/inbox/shared-with-me/life-stories/${encodeURIComponent(inviteId)}` }),
      transformResponse: normalizeSharedLifeStoryResponse
    })
  })
});

export const {
  useGetAllInboxQuery,
  useGetOneInboxQuery,
  useGetSharedWithMeOverviewQuery,
  useGetSharedArtifactCollectionQuery,
  useGetSharedLifeStoryQuery
} = inboxApi;
